import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { handleApiResponse, queryKeys, sharedApi } from "@comandero/shared";
import { showToast } from "@/components/ui/toast";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/I3 · MEDIDO (fase 2, 2026-08-01): estos dos `queryFn` estaban escritos
  // by hand over `openapi-fetch`, which NEVER throws — returns `{data, error}`.
  // With `return data ?? {…}` 503 resolved in SUCCESS with an EMPTY
  // FABRICATED object, so `isError` was never set: `QueryErrorState` below
  // was DEAD CODE (passed `tsc`, showed in diff, never
  // rendered) and I2 `isSuccess` guard did nothing either, because
  // status WAS success. Browser probe with `login-methods` in 503: the
  // screen showed "Google · Not connected" for an account that was connected.
  //
  // `handleApiResponse` THROWS on `{error}` and on an empty body — which is
  // same as `createApiQuery`, way used by other 15 screens
  // with error state in this repo, and only thing making branch reachable.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => handleApiResponse(sharedApi.socialProviders(api)),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => handleApiResponse(sharedApi.loginMethods(api)),
    retry: false,
  }));

  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: without `isSuccess`, reading `.data` with in-flight request
    // SUSPENDS entire section.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2: `?? []` converted failing `login-methods` into "no
  // connected accounts", so screen showed **"Not connected"** for
  // Google account that WAS connected. With `isSuccess` list is only asserted
  // once server answered; error state renders the rest.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  // P23/I3: section is also shown when loading FAILED. If only shown on
  // rendered with `enabledProviders().length > 0`, a failure of
  // `social/providers` would make it vanish completely and error state
  // would become unreachable again — "failed to load" != "empty".
  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/I3: «no hay respuesta» != «no hay nada». El bloque de
              contenido se ENVUELVE (no se encadena): en error `isLoading` es
              `false`, asi que un `when={!q.isLoading && !q.isError}` con
              `fallback={esqueleto}` pintaria error Y esqueleto a la vez. */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              onRetry={() => {
                void providersQuery.refetch();
                void methodsQuery.refetch();
              }}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
